import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes, { number, string } from 'prop-types';

import LineTitle from '../atoms/titles/LineTitle';
import LinkButton from '../atoms/button/LinkButton';
import RoomStateLabel from '../atoms/label/Label';
import Tag from '../atoms/tag/Tag';
import PriceBlock from '../molecules/PriceBlock';
import { makeAdditionalParamsPlanDetail, makeQueryStringsWithDuplicatedSessionKey, makeQueryStringsWithSessionKey } from '../../utils/queryParams';
import { BASENAME, SHOWING_PLAN_LIMIT, PLAN_LIST_HIDE_TAGS } from '../../constants';
import { usePrice } from '../../utils/price';
import ModalPlanOptionSummary from '../modal/ModalPlanOptionSummary';

const FEMALE_TAG_ID = 'female_only';

const selector = (state) => ({
  flight: state.flight,
  planDetail: state.planDetail,
});

const RecommendPlanList = (props) => {
  const {
    handlePlanSelect,
    handleClickPlanDetailLink,
    plans,
    hidePlanListLink,
    handleClickPlanListLink,
    planStartDate,
    maxPlanDisplayNumber,
    editingAccommodation = false,
    paramsPlanList,
    selectedPlanOptions,
  } = props;

  const { flight, planDetail } = useSelector(selector);
  const {
    searchedFlightsInput, selectedOutboundFlight, selectedInboundFlight,
    fetchingOutbound, fetchingInbound, selectedOutboundDate
  } = flight;
  const people = searchedFlightsInput && searchedFlightsInput.people;

  const { plans: selectedPlans } = planDetail;

  const showingPlanLimit = maxPlanDisplayNumber || SHOWING_PLAN_LIMIT;

  const showingPlans = plans ? plans.slice(0, showingPlanLimit) : [];

  const hidePrice = fetchingOutbound || fetchingInbound;
  const priceClassName = hidePrice ? 'hidden' : '';
  const [showPlanOptionModal, setShowPlanOptionModal] = useState(false);
  const [modalSubtitle, setModalSubtitle] = useState('');
  const [modalExplanation, setModalExplanation] = useState('');

  const handleClickPlanListLinkSpan = () => {
    if (handleClickPlanListLink && editingAccommodation) {
      handleClickPlanListLink(plans[0].hotel);
    }else{
      const search = makeQueryStringsWithDuplicatedSessionKey(paramsPlanList);
      const planListLink = `${BASENAME}/plan_list?${search}`;
      window.open(planListLink, '_self');
    }
  };

  return (
    <div className="RecommendPlanList">
      <LineTitle text="おすすめプラン" />
      {showingPlans.map((plan, index) => {
        const {
          id,
          // tieupCompID,
          name,
          stocks,
          recommendationPoint,
          roomType,
          hotel,
          tags: planTags,
          femaleOnlyPlan,
          mealCondition,
          otherItinerary,
          landingFlag,
        } = plan;

        const { id: roomTypeId } = roomType;

        const roomStateLittle = stocks < 7;
        const roomStateLabelColor = roomStateLittle ? 'red' : 'gray';
        const roomStateLabelText = roomStateLittle ? '空室わずか' : '空室あり';

        const subtitle = landingFlag ? otherItinerary.subtitle : '';
        const explanation = landingFlag ? otherItinerary.explanation : '';

        const handleClickPlanOption = (_subtitle, _explanation) => {
          setShowPlanOptionModal(true);
          setModalSubtitle(_subtitle);
          setModalExplanation(_explanation);
        };

        const planDetailParams = makeAdditionalParamsPlanDetail(id, roomTypeId);

        const handleButton = () => {
          const search = editingAccommodation ? makeQueryStringsWithSessionKey(planDetailParams) : makeQueryStringsWithDuplicatedSessionKey(planDetailParams);
          const planDetailLink = `${BASENAME}/plan_detail?${search}`;

          // handleClickPlanDetailLinkがある場合はプラン詳細画面を表示
          if (handleClickPlanDetailLink) {
            return handleClickPlanDetailLink(plan, roomTypeId);
          }
          // handlePlanSelectがある場合もプラン詳細画面を表示するため、handleClickPlanDetailLinkを優先
          if (handlePlanSelect) {
            return handlePlanSelect(plan);
          }
          window.open(planDetailLink, '_blank');
        };

        const { tags: hotelTags } = hotel;

        let tags = [...hotelTags, ...planTags];
        tags = tags.filter(tag => tag.tagGroup && !PLAN_LIST_HIDE_TAGS.includes(tag.tagGroup.name));

        if (femaleOnlyPlan) {
          const femaleTag = {
            id: FEMALE_TAG_ID,
            name: '女性限定',
            tagGroup: {},
          };

          tags.push(femaleTag);
        }

        const { totalPrice, totalPriceIncludePlanOption, pricePerAdult } = usePrice(
          people,
          selectedOutboundFlight,
          selectedInboundFlight,
          selectedPlans.length ? [
            ...selectedPlans.map(p => {
              if (p.startDate === planStartDate) {
                return undefined;
              }

              return p.plan;
            }).filter(Boolean),
            plan,
          ] : [plan],
          selectedOutboundDate,
          selectedPlanOptions,
        );

        return (
          <div className="RecommendPlan" key={`${id}-${roomTypeId}-${index}`}>
            <div className="title" onClick={handleButton}>{name}</div>
            <div className="RecommendPlanBody">
              <div
                className="RecommendPlanText"
                dangerouslySetInnerHTML={{ __html: recommendationPoint }}
              />
              <div className="RecommendPlanIcons">
                <div className="icons">
                {tags && tags.map((t) => {
                  const { id, name, tagGroup } = t;
                  if(tagGroup && 'name' in tagGroup){
                    if (tagGroup.name === '支払方法') {
                      return null;
                    }
                  }

                  return (
                    <div className="tagItem" key={id}>
                      <Tag>{name}</Tag>
                    </div>
                  );
                })}
                {mealCondition && (
                  <div className="tagItem">
                    <Tag>{mealCondition}</Tag>
                  </div>
                )}
                </div>
                {landingFlag ? (
                  <div className="planOptionLabel" onClick={() => handleClickPlanOption(subtitle, explanation)}>
                    <span>{subtitle}</span>
                  </div>
                ) : null}
              </div>
              <div className="RecommendPlanLabel">
                <RoomStateLabel color={roomStateLabelColor}>
                  {roomStateLabelText}
                </RoomStateLabel>
              </div>
            </div>
            <div className="RecommendPlanDetail">
              <div className="icons">
                <div className="tagItems">
                {tags && tags.map((t) => {
                  const { id, name, tagGroup } = t;
                  if(tagGroup && 'name' in tagGroup){
                    if (tagGroup.name === '支払方法') {
                      return null;
                    }
                  }

                  return (
                    <div className="tagItem" key={id}>
                      <Tag>{name}</Tag>
                    </div>
                  );
                })}
                {mealCondition && (
                  <div className="tagItem">
                    <Tag>{mealCondition}</Tag>
                  </div>
                )}
                </div>
                {landingFlag ? (
                  <div className="planOptionLabel" onClick={() => handleClickPlanOption(subtitle, explanation)}>
                    <span>{subtitle}</span>
                  </div>
                ) : null}
              </div>
              <div className="detail">
                <PriceBlock priceTotal={selectedPlanOptions.length > 0 ? totalPriceIncludePlanOption : totalPrice} adultPrice={pricePerAdult} className={priceClassName} editingAccommodation={editingAccommodation} />
                <div className="action">
                  <LinkButton
                    buttonType="cta"
                    onClick={handleButton}
                    disabled={!(selectedOutboundFlight && selectedInboundFlight)}
                  >
                    プランの詳細
                  </LinkButton>
                </div>
              </div>
            </div>
          </div>
        );
      })}
      {(handleClickPlanListLink || !hidePlanListLink) && plans.length > showingPlanLimit && !(showingPlanLimit > SHOWING_PLAN_LIMIT) && (
        <div className="link">
          <div className={`textLinkWrap clickable planListLink`}>
            <span className={`textLink`} onClick={handleClickPlanListLinkSpan}>
              この施設のプラン一覧を見る
            </span>
          </div>
        </div>
      )}
        <ModalPlanOptionSummary
          open={showPlanOptionModal}
          onClose={() => setShowPlanOptionModal(false)}
          subtitle={modalSubtitle}
          explanation={modalExplanation}
          setShowPlanOptionModal={setShowPlanOptionModal}
        />
    </div>
  );

};

RecommendPlanList.defaultProps = {
  plans: [],
  selectedPlanOptions: [],
};

RecommendPlanList.propTypes = {
  handlePlanSelect: PropTypes.func,
  handleClickPlanDetailLink: PropTypes.func,
  plans: PropTypes.array,
  hidePlanListLink: PropTypes.bool,
  handleClickPlanListLink: PropTypes.func,
  planStartDate: string,
  maxPlanDisplayNumber: number,
  editingAccommodation: PropTypes.bool,
  paramsPlanList: PropTypes.object,
  selectedPlanOptions: PropTypes.array,
};

export default RecommendPlanList;
